import React, { useEffect, useState } from "react";
import { IndianRupee, UserPlus } from "lucide-react";
import { AppButton, AppModal, Callout, FieldGroup, FormField, Input, NativeSelect } from "../../ui";
import Shimmer from "../../Common/Shimmer";
import apiClient from "../../../utils/apiClient";
import useAudienceTerm from "../../../hooks/useAudienceTerm";
import { useBranch } from "../../../contexts/BranchContext";
import BatchOptionList from "./BatchOptionList";
import SkippedTable from "./SkippedTable";
import TransferReview from "./TransferReview";
import {
  arrivals, confirmLabel, defaultBatchFor, feeHint, feeNote, groupPreview,
  needsOutcomeView, nextStep, outcomeMessage, readFee,
} from "./addToBatch";

const FORM_ID = "add-to-batch-form";

const errorText = (err, fallback) => err?.response?.data?.message || fallback;

/**
 * AddToBatchModal — put the selected students in a course's batch.
 *
 *   choose  → course, batch (with its seats), and the fee for new enrolments
 *   review  → only when someone already studies the course in another batch
 *             (or the batch would overflow): "transfer them here?", per student
 *   outcome → only when someone could not be added for a reason the admin did
 *             not choose; a clean save closes the dialog and the page says so
 *
 * "Add to batch" asks the server what would happen (POST …/preview). When
 * nobody needs a decision it saves straight away; otherwise it shows the
 * question. The save is re-checked on the server, and it transfers only the
 * students ticked here.
 *
 * Props:
 *   show, onHide
 *   studentIds     — the selection
 *   contextBatchId — opened from a batch's roster: that batch is not offered,
 *                    and its course is chosen to start with
 *   showFees       — the plan has fee management and the caller sees fees
 *   onSaved        — ({ message, result, closed }) after anything was written;
 *                    `closed` is true when the dialog closed itself, so the
 *                    page should say `message`
 */
const AddToBatchModal = ({ show, onHide, studentIds = [], contextBatchId = null, showFees = false, onSaved }) => {
  const terms = useAudienceTerm();
  const { isMultiBranch } = useBranch();
  const [step, setStep] = useState("choose");
  const [options, setOptions] = useState({ loading: true, courses: [], error: "" });
  const [reloadKey, setReloadKey] = useState(0);
  const [courseId, setCourseId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [feeText, setFeeText] = useState("");
  const [preview, setPreview] = useState(null);
  const [transferIds, setTransferIds] = useState(() => new Set());
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const count = studentIds.length;
  const noun = (n) => (n === 1 ? terms.lower : terms.lowerPlural);

  useEffect(() => {
    if (!show) return;
    setStep("choose");
    setCourseId("");
    setBatchId("");
    setFeeText("");
    setPreview(null);
    setTransferIds(new Set());
    setBusy("");
    setError("");
    setResult(null);
  }, [show]);

  // Loaded on every open: seats from the last visit are a guess.
  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setOptions({ loading: true, courses: [], error: "" });
    apiClient.get("/students/add-to-batch/options")
      .then(({ data }) => {
        if (cancelled) return;
        const all = data.courses || [];
        const courses = all
          .map((c) => ({ ...c, batches: c.batches.filter((b) => String(b._id) !== String(contextBatchId)) }))
          .filter((c) => c.batches.length);
        setOptions({ loading: false, courses, error: "" });
        // From a roster, moving people within the course is the likely errand.
        const homeId = contextBatchId
          ? all.find((c) => c.batches.some((b) => String(b._id) === String(contextBatchId)))?._id
          : null;
        const home = courses.find((c) => c._id === homeId);
        if (home) {
          setCourseId(home._id);
          setBatchId(defaultBatchFor(home));
        }
      })
      .catch((err) => {
        if (!cancelled) setOptions({ loading: false, courses: [], error: errorText(err, "Couldn’t load the batches. Check your connection and try again.") });
      });
    return () => { cancelled = true; };
  }, [show, contextBatchId, reloadKey]);

  const course = options.courses.find((c) => c._id === courseId) || null;
  const fee = readFee(feeText);
  const moveIds = () => groupPreview(preview?.students).move.map((r) => r.studentId);

  const changeCourse = (id) => {
    setCourseId(id);
    setBatchId(defaultBatchFor(options.courses.find((c) => c._id === id)));
    setFeeText("");
    setError("");
  };

  const save = async (pv, ids) => {
    setBusy("saving");
    setError("");
    try {
      const { data } = await apiClient.post("/students/add-to-batch", {
        studentIds,
        batchId: pv.batch._id,
        transferIds: [...ids],
        ...(fee.value !== undefined ? { fee: fee.value } : {}),
      });
      const message = outcomeMessage(data, terms);
      const keepOpen = needsOutcomeView(data);
      onSaved?.({ message, result: data, closed: !keepOpen });
      if (!keepOpen) {
        onHide();
        return;
      }
      setResult({ ...data, message });
      setStep("outcome");
    } catch (err) {
      setError(errorText(err, `The ${noun(count)} weren’t added. Check your connection and try again.`));
    }
    setBusy("");
  };

  const check = async (e) => {
    e?.preventDefault();
    if (!batchId || fee.error || busy) return;
    setBusy("checking");
    setError("");
    let data;
    try {
      ({ data } = await apiClient.post("/students/add-to-batch/preview", { studentIds, batchId }));
    } catch (err) {
      setError(errorText(err, "Couldn’t check the batch. Check your connection and try again."));
      setBusy("");
      return;
    }
    setPreview(data);
    setTransferIds(new Set(groupPreview(data.students).move.map((r) => r.studentId)));
    const next = nextStep(data);
    if (next === "save") {
      await save(data, new Set());
      return;
    }
    setStep(next);
    setBusy("");
  };

  const toggle = (id) => setTransferIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const toggleAll = (on) => setTransferIds(on ? new Set(moveIds()) : new Set());
  const back = () => { setStep("choose"); setError(""); };

  let body;
  let actions;

  if (step === "review" && preview) {
    const arrival = arrivals(preview, transferIds);
    body = (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {error && <Callout tone="danger" role="alert">{error}</Callout>}
        <TransferReview
          preview={preview}
          transferIds={transferIds}
          onToggle={toggle}
          onToggleAll={toggleAll}
          feeNote={showFees ? feeNote(fee.value, preview.courseFee) : ""}
          terms={terms}
        />
      </div>
    );
    actions = (
      <>
        <AppButton variant="ghost" className="tw-mr-auto" onClick={back} disabled={!!busy}>Back</AppButton>
        <AppButton variant="outline" onClick={onHide} disabled={!!busy}>Cancel</AppButton>
        <AppButton onClick={() => save(preview, transferIds)} disabled={!!busy || !arrival.arriving || !arrival.fits}>
          {busy === "saving" ? "Saving…" : confirmLabel(arrival, terms)}
        </AppButton>
      </>
    );
  } else if (step === "nothing" && preview) {
    body = (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Callout tone="info">
          Nothing to change in {preview.batch.name} — none of the selected {terms.lowerPlural} can be added to it.
        </Callout>
        <SkippedTable rows={groupPreview(preview.students).skip} terms={terms} />
      </div>
    );
    actions = (
      <>
        <AppButton variant="ghost" className="tw-mr-auto" onClick={back}>Back</AppButton>
        <AppButton onClick={onHide}>Close</AppButton>
      </>
    );
  } else if (step === "outcome" && result) {
    body = (
      <div className="tw-flex tw-flex-col tw-gap-3">
        <Callout tone="success" role="status">{result.message}</Callout>
        <Callout tone="warning">
          {result.skipped.length} {noun(result.skipped.length)} {result.skipped.length === 1 ? "wasn’t" : "weren’t"} added.
        </Callout>
        <SkippedTable rows={result.skipped} terms={terms} />
      </div>
    );
    actions = <AppButton onClick={onHide}>Done</AppButton>;
  } else {
    body = (
      <form id={FORM_ID} onSubmit={check} noValidate className="tw-flex tw-flex-col tw-gap-5">
        <p className="tw-m-0 tw-text-sm tw-leading-normal tw-text-content-muted">
          <span className="tw-font-semibold tw-text-content">{count} {noun(count)}</span> selected. They join the
          batch’s course. Anyone who already studies that course in another batch is shown to you before
          they move.
        </p>

        {error && <Callout tone="danger" role="alert">{error}</Callout>}

        {options.loading ? (
          <Shimmer height={44} count={3} />
        ) : options.error ? (
          <Callout
            tone="danger"
            role="alert"
            action={<AppButton size="xs" variant="outline" onClick={() => setReloadKey((k) => k + 1)}>Try again</AppButton>}
          >
            {options.error}
          </Callout>
        ) : !options.courses.length ? (
          <Callout tone="info">
            There is no upcoming or ongoing batch to add {terms.lowerPlural} to
            {contextBatchId ? " other than this one" : ""}. Create one from Batches first.
          </Callout>
        ) : (
          <>
            <FormField label="Course" required>
              <NativeSelect size="lg" value={courseId} onChange={(e) => changeCourse(e.target.value)}>
                <option value="">Choose a course</option>
                {options.courses.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </NativeSelect>
            </FormField>

            {course && (
              <FieldGroup legend="Batch">
                <BatchOptionList
                  batches={course.batches}
                  value={batchId}
                  onChange={(id) => { setBatchId(id); setError(""); }}
                  showBranch={isMultiBranch}
                />
              </FieldGroup>
            )}

            {course && showFees && (
              <FormField label="Fee for new enrolments" hint={feeHint(course.fee)} error={fee.error}>
                <Input
                  size="lg"
                  inputMode="decimal"
                  autoComplete="off"
                  value={feeText}
                  onChange={(e) => setFeeText(e.target.value)}
                  placeholder={course.fee ? `${course.fee} (course fee)` : "0 (no fee)"}
                  leading={<IndianRupee size={14} aria-hidden="true" />}
                />
              </FormField>
            )}
          </>
        )}
      </form>
    );
    actions = (
      <>
        <AppButton variant="outline" onClick={onHide} disabled={!!busy}>Cancel</AppButton>
        <AppButton type="submit" form={FORM_ID} disabled={!batchId || !!fee.error || !!busy}>
          {busy === "checking" ? "Checking…" : busy === "saving" ? "Adding…" : "Add to batch"}
        </AppButton>
      </>
    );
  }

  return (
    <AppModal
      show={show}
      onHide={busy ? undefined : onHide}
      closeOnOverlay={!busy}
      size="md"
      title="Add to batch"
      icon={<UserPlus size={18} aria-hidden="true" />}
      footer={<div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">{actions}</div>}
    >
      {body}
    </AppModal>
  );
};

export default AddToBatchModal;
